import Details from "@booking/components/containers/Details";
import { usePublish } from "@booking/hooks/usePubSub";
import type { DropdownOptionType, DropdownType } from "@booking/types";
import {
  type GaSelectContentParams,
  gaSelectContent,
} from "@booking/utils/tracking";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import React, { useState } from "react";

import Icon from "@/components/atoms/media/Icon";
import { IconEnum, SizeEnum } from "@/enums";
import type { CommonProps } from "@/types";
import { createId, datasetFlag } from "@/utils/helpers";

import styles from "./Dropdown.module.css";

const ref = "dropdown";

enum DropdownEventEnum {
  CHANGE = "dropdownChange",
}

interface DropdownProps extends Partial<CommonProps>, DropdownType {
  custom?: boolean;
  gaSelectContentParams?: Partial<GaSelectContentParams>;
  icon?: IconDefinition | IconEnum;
  invalid?: boolean;
  label: string;
  labelInside?: boolean;
  noLabel?: boolean;
  options: Array<DropdownOptionType>;
}

function focusSummary(label: HTMLLabelElement) {
  const summary = label.querySelector<HTMLElement>("summary");

  if (summary) {
    summary.focus();
  }
}

function onclick(e: React.MouseEvent<HTMLElement>) {
  const li = (e.target as HTMLElement).closest<HTMLLIElement>("li");

  if (!li) {
    return;
  }

  const value = li.dataset.alt || li.innerHTML;
  const label = li.closest<HTMLLabelElement>("label");

  if (label) {
    const select = label.querySelector<HTMLSelectElement>("select");

    if (select) {
      const { childNodes } = li.parentNode as HTMLUListElement;

      childNodes.forEach((child: Node) => {
        delete (child as HTMLLIElement).dataset.checked;
      });

      const selected = [...childNodes].find((child) => child === li);

      if (selected) {
        (selected as HTMLLIElement).dataset.checked = "";
        select.value = value;
        select.dispatchEvent(
          new Event("change", { bubbles: true, cancelable: true }),
        );
      }
    }

    const output = label.querySelector<HTMLOutputElement>("output");

    if (output) {
      output.value = value;
    }

    const details = label.querySelector<HTMLDetailsElement>("details");

    if (details && "controls" in details.dataset) {
      details.removeAttribute("open");
    }

    focusSummary(label);
  }
}

function onlabelclick(e: React.MouseEvent<HTMLElement>) {
  const label = (e.target as HTMLElement).closest("label");

  if (label) {
    focusSummary(label);
  }
}

function renderCustom(
  id: string,
  options: Array<DropdownOptionType>,
  size: number,
  value?: string,
) {
  const open = size > 1;

  return (
    <Details
      noControl={open}
      open={open}
      summary={renderSummary(id, options, value)}
    >
      <ul>{renderCustomOptions(id, options, value)}</ul>
    </Details>
  );
}

function renderCustomOptions(
  id: string,
  options: Array<DropdownOptionType>,
  value?: string,
) {
  return options.map(({ alt, disabled, text, value: optionValue }, index) => {
    const key = `${id}_option_${index}`;

    return (
      <li
        key={key}
        data-alt={alt && text ? text : undefined}
        data-disabled={datasetFlag(!!disabled)}
        data-checked={datasetFlag(value === optionValue)}
        onClick={disabled ? undefined : onclick}
      >
        {alt || text}
      </li>
    );
  });
}

function renderOptions(id: string, options: Array<DropdownOptionType>) {
  return options.map(({ alt, disabled, text, value }, index) => (
    <option key={`${id}_${index}`} disabled={disabled} value={value}>
      {alt || text}
    </option>
  ));
}

function renderSummary(
  id: string,
  options: Array<DropdownOptionType>,
  value?: string,
) {
  const { text } =
    options.find((option) => option.value === value) || options[0];

  return (
    <output htmlFor={id} aria-live="polite">
      {text}
    </output>
  );
}

function useDropdown(props: DropdownProps) {
  const { color, disabled, icon, options = [], size = 1, labelInside } = props;
  const className = clsx(
    styles[ref],
    color && styles[color],
    disabled && styles.disabled,
    icon && styles.icon,
    props.invalid && styles.invalid,
    props.noLabel && styles["no-label"],
    labelInside && styles["label-inside"],
    props.className,
  );
  const id = props.id || createId(ref);
  const selectSize =
    size <= 1 ? 0 : Math.max(2, Math.min(size, options.length || 6));

  return {
    className,
    disabled: !!disabled,
    icon,
    id,
    options,
    size: selectSize,
  };
}

function Dropdown(props: DropdownProps) {
  function onchange(e: React.ChangeEvent<HTMLSelectElement>) {
    const { options, selectedIndex, value } = e.target;
    const { text } = options[selectedIndex];
    const { gaSelectContentParams } = props;

    setValue(value);
    gaSelectContent({
      contentType: "Dropdown",
      itemListName: props.label,
      itemName: text,
      ...gaSelectContentParams,
    });
    publish(DropdownEventEnum.CHANGE, { value });
  }

  const { className, disabled, icon, id, options, size } = useDropdown(props);
  const { custom, name = id, required } = props;
  const publish = usePublish({ context: name, ...props.pubSubData });
  const [value, setValue] = useState(props.value || "");

  return options.length === 0 ? null : (
    <label
      className={className}
      htmlFor={id}
      onClick={custom ? onlabelclick : undefined}
    >
      <span data-component="label">{props.label}</span>
      <select
        autoFocus={(props.autofocus && !custom) || undefined}
        disabled={disabled}
        hidden={custom}
        id={id}
        name={name}
        onChange={onchange}
        required={required}
        size={size}
        value={value}
      >
        {renderOptions(id, options)}
      </select>

      {custom && renderCustom(id, options, size, value)}

      {icon && <Icon name={icon} size={SizeEnum.LARGE} />}
    </label>
  );
}

export default Dropdown;
export { Dropdown, type DropdownProps, DropdownEventEnum };
